export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'zula-theme';

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Reads the persisted theme choice, defaulting to 'system' for anyone who has
 * never opened Settings -- since 'system' already tracks prefers-color-scheme,
 * this reproduces today's behavior exactly, with zero visual regression for
 * users who haven't made an explicit choice yet.
 */
export function getStoredThemeMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  return isThemeMode(stored) ? stored : 'system';
}

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Whether [mode] currently resolves to the dark palette. */
export function resolvesToDark(mode: ThemeMode): boolean {
  return mode === 'dark' || (mode === 'system' && systemPrefersDark());
}

/** Applies (or removes) the `.dark` class the tokens in index.css key off of. */
export function applyThemeMode(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', resolvesToDark(mode));
}

/** Persists the user's choice and applies it immediately. */
export function setThemeMode(mode: ThemeMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
  applyThemeMode(mode);
}

/**
 * Resolves and applies the persisted theme, then keeps 'system' mode tracking
 * the OS preference live (no reload required). Call once, synchronously,
 * before/during initial render (see main.tsx) so there's no flash of the
 * wrong theme for users with an explicit light/dark choice.
 */
export function initTheme(): void {
  applyThemeMode(getStoredThemeMode());
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    // Re-read the stored mode rather than closing over it: it may have
    // changed (e.g. the user picked Light/Dark in another tab) since init.
    if (getStoredThemeMode() === 'system') applyThemeMode('system');
  });
}
